import React, { useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { MapPin, Phone, Clock, Send, CheckCircle, Facebook, MessageCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [form, setForm] = useState({ name: '', phone: '', email: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError('');
    try {
      await axios.post(`${API_URL}/api/contacts`, form);
      setSuccess(true);
      setForm({ name: '', phone: '', email: '', subject: '', message: '' });
    } catch (err) {
      setError('Erreur lors de l\'envoi. Veuillez réessayer ou nous appeler.');
    } finally {
      setSending(false);
    }
  };

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  return (
    <div style={{ background: 'var(--bg-primary)', fontFamily: 'var(--font-body)' }} data-testid="contact-page">
      {/* Hero - compact */}
      <section className="relative py-16 lg:py-20" style={{ background: 'var(--bg-surface)' }}>
        <div className="max-w-7xl mx-auto px-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="section-label">Contact</span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mt-3 mb-4 tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
              Contactez<span className="text-[#DC2626]">-nous</span>
            </h1>
            <p className="text-zinc-400 max-w-xl">Une question ? Un devis ? Notre équipe vous répond dans les plus brefs délais.</p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 lg:py-24" style={{ background: 'var(--bg-primary)' }}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            {/* Contact Info */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h2 className="text-lg font-bold text-white mb-6">Nos Coordonnées</h2>
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-white/5 border border-white/10 flex-shrink-0">
                      <MapPin size={18} className="text-[#DC2626]" />
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm mb-0.5">Adresse</p>
                      <p className="text-zinc-500 text-sm">32 Rue Marcellin Berthelot<br />59243 Quarouble</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-white/5 border border-white/10 flex-shrink-0">
                      <Phone size={18} className="text-[#DC2626]" />
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm mb-0.5">Téléphone</p>
                      <a href="tel:+33613851946" className="text-[#DC2626] font-semibold text-sm hover:underline">06 13 85 19 46</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-white/5 border border-white/10 flex-shrink-0">
                      <Clock size={18} className="text-[#DC2626]" />
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm mb-0.5">Horaires d'ouverture</p>
                      <p className="text-zinc-500 text-sm">Lundi - Vendredi: 9h - 12h / 14h - 18h</p>
                      <p className="text-zinc-500 text-sm">Samedi: Sur rendez-vous</p>
                      <p className="text-zinc-500 text-sm">Dimanche: Fermé</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-white/5 border border-white/10 flex-shrink-0">
                      <Facebook size={18} className="text-[#DC2626]" />
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm mb-0.5">Réseaux sociaux</p>
                      <a href="https://www.facebook.com/share/1CBFASZRBu/" target="_blank" rel="noopener noreferrer" className="text-zinc-500 text-sm hover:text-white transition-colors">Suivez-nous sur Facebook</a>
                    </div>
                  </div>
                </div>
              </div>
              {/* Map */}
              <div className="border border-white/5 overflow-hidden h-[220px]">
                <iframe title="MCA CAR - Quarouble" src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2539.8!2d3.5988!3d50.3838!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47c2e8c0!2s32+Rue+Marcellin+Berthelot+59243+Quarouble!5e0!3m2!1sfr!2sfr!4v1"
                  width="100%" height="100%" style={{ border: 0, filter: 'invert(0.9) hue-rotate(180deg) brightness(0.8) contrast(1.2)' }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-3">
              <div className="border border-white/5 p-6 lg:p-8" style={{ background: 'var(--bg-surface)' }}>
                <h2 className="text-lg font-bold text-white mb-1">Envoyez-nous un message</h2>
                <p className="text-zinc-500 text-sm mb-6">Réponse sous 24h. Tous les champs marqués * sont obligatoires.</p>

                {success ? (
                  <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-10" data-testid="contact-success">
                    <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-green-500/10 border border-green-500/20 rounded-full">
                      <CheckCircle size={32} className="text-green-500" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">Message envoyé !</h3>
                    <p className="text-zinc-500 text-sm mb-6">Nous vous répondrons dans les plus brefs délais.</p>
                    <button onClick={() => setSuccess(false)} className="text-[#DC2626] text-sm font-semibold hover:underline" data-testid="contact-new-message">Envoyer un autre message</button>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="contact-form space-y-4" data-testid="contact-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-1.5">Nom complet *</label>
                        <input type="text" required value={form.name} onChange={e => updateField('name', e.target.value)} placeholder="Jean Dupont" data-testid="contact-name" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-1.5">Téléphone *</label>
                        <input type="tel" required value={form.phone} onChange={e => updateField('phone', e.target.value)} placeholder="06 12 34 56 78" data-testid="contact-phone" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-1.5">Email *</label>
                      <input type="email" required value={form.email} onChange={e => updateField('email', e.target.value)} placeholder="jean.dupont@email.com" data-testid="contact-email" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-1.5">Sujet *</label>
                      <select required value={form.subject} onChange={e => updateField('subject', e.target.value)} data-testid="contact-subject">
                        <option value="">Choisir un sujet</option>
                        <option value="devis">Demande de devis</option>
                        <option value="rdv">Prise de rendez-vous</option>
                        <option value="vehicule">Renseignement véhicule</option>
                        <option value="piece">Recherche de pièce</option>
                        <option value="autre">Autre demande</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-1.5">Message *</label>
                      <textarea required rows={4} value={form.message} onChange={e => updateField('message', e.target.value)} placeholder="Décrivez votre demande..." className="resize-none" data-testid="contact-message" />
                    </div>
                    {error && <p className="text-red-400 text-sm" data-testid="contact-error">{error}</p>}
                    <button type="submit" disabled={sending} className="w-full inline-flex items-center justify-center gap-2 py-3.5 bg-[#DC2626] text-white font-semibold text-sm transition-all hover:bg-[#B91C1C] disabled:opacity-50" data-testid="contact-submit">
                      {sending ? 'Envoi en cours...' : <><Send size={16} /> Envoyer le message</>}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Sticky Bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 border-t border-white/10 flex" style={{ background: 'var(--bg-primary)' }}>
        <a href="tel:+33613851946" className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-[#DC2626] text-white font-semibold text-sm">
          <Phone size={16} /> Appeler
        </a>
        <a href="https://wa.me/33613851946" target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-green-600 text-white font-semibold text-sm">
          <MessageCircle size={16} /> WhatsApp
        </a>
      </div>
    </div>
  );
};

export default Contact;
